import { useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent,
    DialogTitle, TextField, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface DeleteGroupDialogProps {
    groupName: string;
    onDeleteGroup: () => void;
    onCloseDeleteGroupDialogBox: () => void;
    onCloseGroupDetailsModal: () => void;
}

export function DeleteGroupDialogBox({ groupName, onDeleteGroup, onCloseDeleteGroupDialogBox, onCloseGroupDetailsModal }: DeleteGroupDialogProps) {
    const [inputValue, setInputValue] = useState('');
    const confirmKey = groupName.slice(0, 5);
    const confirmed  = inputValue === confirmKey;

    function handleConfirmDelete() {
        if (confirmed) {
            onDeleteGroup();
            onCloseDeleteGroupDialogBox();
            onCloseGroupDetailsModal();
        }
    }

    return (
        <Dialog
            open
            maxWidth='xs'
            fullWidth
            sx={{ zIndex: 1400 }}
            PaperProps={{ sx: { bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                <WarningAmberIcon sx={{ fontSize: 40, color: BRAND_RED, display: 'block', mx: 'auto', mb: 1 }} />
                <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>Delete Group</Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant='body2' color={TEXT_SECONDARY} textAlign='center' mb={2.5}>
                    Type <strong style={{ color: TEXT_PRIMARY }}>&apos;{confirmKey}&apos;</strong> to confirm. This action cannot be undone.
                </Typography>
                <TextField
                    fullWidth
                    size='small'
                    placeholder={confirmKey}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: TEXT_PRIMARY,
                            bgcolor: '#24242e',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
                            '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                        },
                        '& input': { textAlign: 'center' },
                    }}
                />
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2.5, px: 3 }}>
                <Button
                    variant='text'
                    onClick={onCloseDeleteGroupDialogBox}
                    sx={{ color: TEXT_SECONDARY, textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    disabled={!confirmed}
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleConfirmDelete}
                    sx={{
                        bgcolor: BRAND_RED,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#a93226' },
                        '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.3)', color: 'rgba(255,255,255,0.3)' },
                    }}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}
